import { Staff } from '../../types';
import { DepartmentResponse } from '../../types';
import { NoharaRepresentative } from '../../types/company.types';
import { SaleStaff } from '../../types/table/company.types';

// Helper function to extract staffs from the new staffsByDepartment structure
export const getStaffsFromDepartment = (
  staffsByDepartment: Record<string, Staff[] | Record<string, Staff[]>>,
  departmentId: string
): Staff[] => {
  const departmentData = staffsByDepartment[departmentId];
  if (!departmentData) return [];

  // If it's an array (old format), return it directly
  if (Array.isArray(departmentData)) {
    return departmentData;
  }

  // If it's an object (new format), return all staffs from all roles
  const allStaffs: Staff[] = [];
  Object.values(departmentData).forEach((staffs) => {
    if (Array.isArray(staffs)) {
      allStaffs.push(...staffs);
    }
  });
  return allStaffs;
};

/**
 * Maps form representatives to API departmentContactLists format
 * Converts form field names (IDs) to API field names (codes)
 * Used in construction, witness, customer, and supplier mappers
 */
export const mapRepresentativesToDepartmentContactLists = (
  representatives: NoharaRepresentative[],
  departments: DepartmentResponse[] = []
): Array<{ contactPersonCode: number; departmentCode: number }> => {
  return representatives
    .filter((rep) => rep.department && rep.salesPerson)
    .map((rep) => {
      const department = departments.find((dept) => dept.id === rep.department);
      const staff = rep.salesPerson;
      const departmentCode = department?.code ?? rep.departmentCode;
      if (departmentCode && staff?.code) {
        return {
          contactPersonCode: Number(staff.code),
          departmentCode: Number(departmentCode),
        };
      }
      return null;
    })
    .filter(Boolean) as Array<{ contactPersonCode: number; departmentCode: number }>;
};

/**
 * Maps API departmentContactLists to form representatives format
 * Converts API field names (codes) to form field names (IDs)
 * Used when loading supplier data from API
 */
export const mapDepartmentContactListsToRepresentatives = (
  departmentContactLists:
    | Array<{
        departmentCode: number;
        departmentId?: string;
        departmentName?: string;
        contactPersonCode: number;
        saleStaff?: SaleStaff;
        canUpdate?: boolean;
        canView?: boolean;
      }>
    | null
    | undefined,
  departments: DepartmentResponse[] = []
): NoharaRepresentative[] => {
  if (!departmentContactLists || departmentContactLists.length === 0) {
    return [];
  }

  return departmentContactLists.map((contact, index) => {
    const departmentId =
      departments.find((dept) => dept.code === contact.departmentCode)?.id ??
      contact.departmentId ??
      '';

    // If department is missing or not found, clear salesPerson to make it an empty representative
    // This ensures validation will skip it (empty representatives are skipped)
    const hasValidDepartment = !!departmentId;

    const saleStaff = contact.saleStaff;
    return {
      department: departmentId,
      departmentCode: contact.departmentCode.toString(),
      departmentName: contact.departmentName,
      salesPerson:
        hasValidDepartment && saleStaff
          ? {
              id: saleStaff.id,
              firstName: saleStaff.firstName || '',
              lastName: saleStaff.lastName || '',
              code: String(contact.contactPersonCode ?? ''),
            }
          : {
              id: '',
              firstName: '',
              lastName: '',
              code: '',
            },
      canUpdate: contact.canUpdate,
      canView: contact.canView,
      originalIndex: index,
    };
  });
};
